/**
 * Auth hooks for authentication state and operations.
 */

import { useState, useCallback } from 'react';
import {
  getSession,
  logout as apiLogout,
  getProfile,
  initiateGoogleLogin,
  initiateMicrosoftLogin,
  type UserProfile,
} from '@/api/auth';

export interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isSuperadmin: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface UseAuthReturn extends AuthState {
  loginWithGoogle: (redirectTo?: string) => Promise<void>;
  loginWithMicrosoft: (redirectTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  clearError: () => void;
}

/**
 * Hook for managing authentication state and operations.
 */
export function useAuthState(): UseAuthReturn {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const session = await getSession();

      if (session.meta.is_authenticated && session.data.user) {
        // Fetch profile (includes is_superadmin)
        const profile = await getProfile();
        setUser(profile);
        setIsSuperadmin(profile.is_superadmin ?? false);
      } else {
        setUser(null);
        setIsSuperadmin(false);
      }
    } catch (err) {
      console.error('Failed to refresh auth:', err);
      setUser(null);
      setIsSuperadmin(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loginWithGoogle = useCallback(async (redirectTo?: string) => {
    // Redirect to Google OAuth flow via form POST
    // After successful auth, user will be redirected back and refreshAuth will pick up the session
    const callbackUrl = window.location.origin + (redirectTo || '/');
    await initiateGoogleLogin(callbackUrl);
  }, []);

  const loginWithMicrosoft = useCallback(async (redirectTo?: string) => {
    // Redirect to Microsoft OAuth flow via form POST
    // Works for Outlook, Hotmail, Live, Microsoft 365 accounts
    const callbackUrl = window.location.origin + (redirectTo || '/');
    await initiateMicrosoftLogin(callbackUrl);
  }, []);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      await apiLogout();
    } catch (err) {
      // Ignore errors - user might already be logged out
      console.debug('Logout API call failed:', err);
    } finally {
      // Always clear local state
      setUser(null);
      setIsSuperadmin(false);
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    user,
    isAuthenticated: !!user,
    isSuperadmin,
    isLoading,
    error,
    loginWithGoogle,
    loginWithMicrosoft,
    logout,
    refreshAuth,
    clearError,
  };
}
